import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { MessageCircle, Send, User, Bot, Trash2, Plus, Loader2, Minimize2, Maximize2, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { AuthContext } from '../contexts/AuthContext';
import ApiService from '../services/api';
import MAILogo from './ui/MAILogo';

const AIChatAssistant = ({ isMinimized, onToggleMinimize }) => {
  const { user } = useContext(AuthContext);
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadSessions = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await ApiService.request('GET', `/users/${user.id}/chat/sessions`);
      if (response.success) {
        setSessions(response.data.sessions);
        // Auto-select first session if exists
        if (response.data.sessions.length > 0 && !currentSession) {
          setCurrentSession(response.data.sessions[0]);
          loadMessages(response.data.sessions[0].session_id);
        }
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  }, [user?.id, currentSession]);

  useEffect(() => {
    if (user?.id) {
      loadSessions();
    }
  }, [user?.id, loadSessions]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async (sessionId) => {
    try {
      const response = await ApiService.request('GET', `/users/${user.id}/chat/sessions/${sessionId}/messages`);
      if (response.success) {
        setMessages(response.data.messages);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const createNewSession = async () => {
    setCreating(true);
    try {
      const response = await ApiService.request('POST', `/users/${user.id}/chat/sessions`, {
        session_name: `Military Benefits Chat - ${new Date().toLocaleDateString()}`
      });
      
      if (response.success) {
        const newSession = {
          session_id: response.data.session_id,
          session_name: `Military Benefits Chat - ${new Date().toLocaleDateString()}`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setSessions(prev => [newSession, ...prev]);
        setCurrentSession(newSession);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error creating session:', error);
    } finally {
      setCreating(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentSession || loading) return;

    const userMessage = newMessage;
    setNewMessage('');
    setLoading(true);

    // Add user message to UI immediately
    const tempUserMessage = {
      message_id: 'temp-' + Date.now(),
      message_type: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      const response = await ApiService.request('POST', `/users/${user.id}/chat/sessions/${currentSession.session_id}/messages`, {
        message: userMessage
      });

      if (response.success) {
        // Replace temp message with actual messages from server
        setMessages(prev => {
          const filtered = prev.filter(msg => msg.message_id !== tempUserMessage.message_id);
          return [...filtered, response.data.user_message, response.data.ai_response];
        });
      } else {
        // Handle error - replace temp message with error
        setMessages(prev => {
          const filtered = prev.filter(msg => msg.message_id !== tempUserMessage.message_id);
          return [...filtered, tempUserMessage, {
            message_id: 'error-' + Date.now(),
            message_type: 'system',
            content: 'Sorry, I encountered an error processing your message. Please try again.',
            timestamp: new Date().toISOString()
          }];
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Handle error
      setMessages(prev => {
        const filtered = prev.filter(msg => msg.message_id !== tempUserMessage.message_id);
        return [...filtered, tempUserMessage, {
          message_id: 'error-' + Date.now(),
          message_type: 'system',
          content: 'Network error. Please check your connection and try again.',
          timestamp: new Date().toISOString()
        }];
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      const response = await ApiService.request('DELETE', `/users/${user.id}/chat/sessions/${sessionId}`);
      if (response.success) {
        setSessions(prev => prev.filter(s => s.session_id !== sessionId));
        if (currentSession?.session_id === sessionId) {
          setCurrentSession(null);
          setMessages([]);
        }
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const switchSession = (session) => {
    setCurrentSession(session);
    loadMessages(session.session_id);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 left-4 z-50">
        <div className="relative group">
          <Button
            onClick={onToggleMinimize}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full w-14 h-14 shadow-lg border-2 border-blue-400 transition-all duration-300 hover:shadow-xl hover:scale-105"
          >
            <MAILogo className="w-8 h-8" />
          </Button>
          
          {/* Up Arrow to indicate "open/expand" */}
          <div className="absolute -top-2 -right-2 bg-white rounded-full w-6 h-6 flex items-center justify-center shadow-md border border-blue-200 group-hover:bg-blue-50 transition-colors duration-200">
            <ChevronUp className="w-4 h-4 text-blue-600 group-hover:text-blue-700" />
          </div>
          
          {/* Tooltip */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="bg-slate-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
              Open MilConnect AI
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 z-50 w-96 h-[500px] shadow-2xl">
      <Card className="h-full flex flex-col border-2 border-blue-400 bg-gradient-to-b from-slate-50 to-blue-50 text-slate-800 relative">
        {/* Quick Minimize Button - Floating on top right */}
        <div className="absolute top-2 right-2 z-10 group">
          <Button
            onClick={onToggleMinimize}
            variant="ghost"
            size="sm"
            className="bg-white/80 backdrop-blur-sm hover:bg-white text-slate-600 hover:text-slate-800 h-6 w-6 p-0 rounded-full shadow-sm border border-slate-200 hover:border-slate-300 transition-all duration-200"
            title="Minimize Chat"
          >
            <ChevronDown className="w-3 h-3" />
          </Button>
          
          {/* Tooltip */}
          <div className="absolute top-full right-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="bg-slate-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
              Minimize Chat
            </div>
          </div>
        </div>
        
        <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg py-3 border-b border-blue-400">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MAILogo className="w-6 h-6" />
              <CardTitle className="text-lg text-white font-semibold">MilConnect AI</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={createNewSession}
                disabled={creating}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-blue-500/20 h-8 w-8 p-0"
                title="New Chat Session"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
              <Button
                onClick={onToggleMinimize}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-blue-500/20 h-8 w-8 p-0 relative"
                title="Minimize Chat"
              >
                <ChevronDown className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <div className="flex flex-1 min-h-0">
          {/* Sessions Sidebar */}
          <div className="w-24 bg-slate-100 border-r border-blue-200 flex flex-col">
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {sessions.map((session) => (
                <div
                  key={session.session_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => switchSession(session)}
                  onKeyDown={(e) => e.key === 'Enter' && switchSession(session)}
                  className={`w-full p-2 rounded text-xs transition-colors relative group cursor-pointer ${
                    currentSession?.session_id === session.session_id
                      ? 'bg-blue-100 text-blue-600 border border-blue-300'
                      : 'bg-white hover:bg-slate-50 text-slate-600 border border-slate-200'
                  }`}
                >
                  <MAILogo className="w-4 h-4 mx-auto mb-1" />
                  <div className="truncate">
                    {new Date(session.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSession(session.session_id);
                    }}
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3 text-red-500 hover:text-red-600" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col">
            {currentSession ? (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.length === 0 && (
                    <div className="text-center text-slate-600 py-6">
                      <MAILogo className="w-16 h-16 mx-auto mb-4" />
                      <p className="text-lg font-semibold text-slate-800 mb-2">Welcome to MilConnect AI!</p>
                      <p className="text-sm text-blue-600 mb-4">
                        Your intelligent military benefits assistant
                      </p>
                      
                      {/* Quick starter questions */}
                      <div className="space-y-2 mb-4">
                        {[
                          "What's the difference between TRICARE Prime and Select?",
                          "How do I transfer my GI Bill benefits?",
                          "What VA benefits am I eligible for?",
                          "How does military housing allowance work?"
                        ].map((question) => (
                          <button
                            key={question}
                            onClick={() => {
                              setNewMessage(question);
                              document.querySelector('input[placeholder*="Ask about military benefits"]')?.focus();
                            }}
                            className="block w-full text-xs text-left text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-2 rounded border border-blue-200 hover:border-blue-300 transition-colors"
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                      
                      <div className="text-xs text-slate-500 space-y-1">
                        <p>Also available: SGLI, BAH/BAS, Career Transition, Family Support</p>
                      </div>
                    </div>
                  )}
                  
                  {messages.map((message) => (
                    <div
                      key={message.message_id}
                      className={`flex gap-3 ${
                        message.message_type === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      {message.message_type === 'assistant' && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                          <MAILogo className="w-5 h-5" />
                        </div>
                      )}
                      <div
                        className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                          message.message_type === 'user'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-slate-800 border border-slate-200'
                        }`}
                      >
                        {message.content}
                      </div>
                      {message.message_type === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-slate-600" />
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {loading && (
                    <div className="flex gap-3 justify-start">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
                        <MAILogo className="w-5 h-5" />
                      </div>
                      <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                          <span className="text-slate-800">Thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={sendMessage} className="border-t border-blue-200 p-4 bg-white">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Ask about military benefits..."
                      className="flex-1 px-3 py-2 border border-slate-300 text-slate-800 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm placeholder-slate-400"
                      disabled={loading}
                    />
                    <Button
                      type="submit"
                      disabled={loading || !newMessage.trim()}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 border border-blue-600"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-4">
                <div>
                  <MAILogo className="w-12 h-12 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">No chat sessions yet</p>
                  <Button onClick={createNewSession} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Start New Chat
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AIChatAssistant;